import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Inbox, Check, X, Briefcase, ArrowLeft, Mail, Phone, ExternalLink } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, formatApiError } from "@/lib/api";

export default function NdaInbox() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [filter, setFilter] = useState("pending");
    const [notes, setNotes] = useState({}); // {requestId: "..."}

    const load = () => {
        const q = filter === "all" ? "" : `?status=${filter}`;
        api.get(`/my/nda-requests/incoming${q}`).then(({ data }) => setData(data)).catch(() => setData({ items: [], summary: {} }));
    };
    useEffect(load, [filter]);

    const decide = async (req, decision) => {
        const note = notes[req.id] || "";
        try {
            await api.patch(`/businesses/${req.business_id}/nda-requests/${req.id}`, { decision, note });
            toast.success(`NDA ${decision}.`);
            setNotes((n) => { const c = { ...n }; delete c[req.id]; return c; });
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed");
        }
    };

    if (!data) return null;
    const { items = [], summary = {} } = data;

    return (
        <div data-testid="nda-inbox-page" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="mb-4">
                <ArrowLeft className="h-4 w-4 mr-1" /> Dashboard
            </Button>
            <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-coral font-app font-semibold mb-1">Seller tools</div>
                    <h1 className="font-display text-4xl font-bold flex items-center gap-2">
                        <Inbox className="h-7 w-7 text-coral" /> NDA Inbox
                    </h1>
                    <p className="text-muted-foreground font-app mt-1 max-w-2xl">
                        Approve or reject NDA requests from buyers. Approved buyers immediately unlock revenue, EBITDA, and your full pitch.
                    </p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                    <Card className="p-3 text-center min-w-[80px]"><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Pending</div><div className="font-display text-2xl font-bold text-coral">{summary.pending || 0}</div></Card>
                    <Card className="p-3 text-center min-w-[80px]"><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Approved</div><div className="font-display text-2xl font-bold text-fiji-green">{summary.approved || 0}</div></Card>
                    <Card className="p-3 text-center min-w-[80px]"><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Rejected</div><div className="font-display text-2xl font-bold text-muted-foreground">{summary.rejected || 0}</div></Card>
                </div>
            </div>

            <Tabs value={filter} onValueChange={setFilter} className="mb-5">
                <TabsList>
                    <TabsTrigger value="pending" data-testid="filter-pending">Pending</TabsTrigger>
                    <TabsTrigger value="approved" data-testid="filter-approved">Approved</TabsTrigger>
                    <TabsTrigger value="rejected" data-testid="filter-rejected">Rejected</TabsTrigger>
                    <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
                </TabsList>
            </Tabs>

            {items.length === 0 ? (
                <Card className="p-12 text-center">
                    <Inbox className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                    <p className="font-app text-muted-foreground">No {filter !== "all" ? filter : ""} NDA requests.</p>
                </Card>
            ) : (
                <div className="space-y-3" data-testid="nda-list">
                    {items.map((req) => (
                        <Card key={req.id} className="p-5" data-testid={`nda-${req.id}`}>
                            <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                        <Briefcase className="h-4 w-4 text-ocean dark:text-turquoise" />
                                        <Link to={`/businesses/${req.business_id}`} className="font-app text-sm font-semibold hover:underline truncate">
                                            {req.business_title}
                                        </Link>
                                        <Badge className={
                                            req.status === "pending" ? "bg-coral/15 text-coral border-0 text-[10px] uppercase font-app" :
                                            req.status === "approved" ? "bg-fiji-green/15 text-fiji-green border-0 text-[10px] uppercase font-app" :
                                            "bg-secondary text-muted-foreground border-0 text-[10px] uppercase font-app"
                                        }>{req.status}</Badge>
                                    </div>
                                    <div className="text-xs font-app text-muted-foreground">
                                        {new Date(req.created_at).toLocaleString()}
                                    </div>
                                </div>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4 text-sm font-app mb-4">
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Buyer</div>
                                    <div className="font-medium">{req.buyer_full_name}</div>
                                    {req.buyer_company && <div className="text-xs text-muted-foreground">{req.buyer_company}</div>}
                                    <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><Mail className="h-3 w-3" />{req.buyer_email}</div>
                                    {req.buyer_phone && <div className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="h-3 w-3" />{req.buyer_phone}</div>}
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Purpose</div>
                                    <div className="text-sm whitespace-pre-line">{req.purpose}</div>
                                </div>
                            </div>
                            {req.status === "pending" ? (
                                <div className="flex items-center gap-2 flex-wrap pt-3 border-t">
                                    <Input
                                        placeholder="Optional note to the buyer (e.g. counter-questions)…"
                                        value={notes[req.id] || ""}
                                        onChange={(e) => setNotes((n) => ({ ...n, [req.id]: e.target.value }))}
                                        className="flex-1 min-w-[200px]"
                                        data-testid={`note-${req.id}`}
                                    />
                                    <Button onClick={() => decide(req, "approved")} className="bg-fiji-green text-white" size="sm" data-testid={`approve-${req.id}`}>
                                        <Check className="h-4 w-4 mr-1" /> Approve
                                    </Button>
                                    <Button onClick={() => decide(req, "rejected")} variant="outline" size="sm" data-testid={`reject-${req.id}`}>
                                        <X className="h-4 w-4 mr-1" /> Reject
                                    </Button>
                                </div>
                            ) : req.note ? (
                                <div className="text-xs font-app text-muted-foreground pt-3 border-t">
                                    Your note: <span className="italic">"{req.note}"</span>
                                </div>
                            ) : null}
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
